import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Plus, Loader2, MapPin } from "lucide-react";
import api from "../../../lib/api";
import { formatPrice } from "../../../lib/formatPrice";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { useLabBooking } from "../context/LabBookingContext";
import { POPULAR_CHIPS } from "../mock/mockData";
import LocationModal from "./LocationModal";

const LabSearchBar = () => {
  const [query, setQuery]     = useState("");
  const [open, setOpen]       = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const containerRef          = useRef(null);
  const { addTest, isTestSelected, removeTest, location } = useLabBooking();

  const [showLocationModal, setShowLocationModal] = useState(false);

  const closeDropdown = useCallback(() => setOpen(false), []);
  useOnClickOutside(containerRef, closeDropdown);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      const name = query.trim();
      Promise.all([
        api.post("/user/lab/master-test",    { name, page: 1 }),
        api.post("/user/lab/master-package", { name, page: 1 }),
      ])
        .then(([testRes, packageRes]) => {
          const tests    = (testRes.data?.data?.items    || []).map((r) => ({ ...r, kind: "test"    }));
          const packages = (packageRes.data?.data?.items || []).map((r) => ({ ...r, kind: "package" }));
          setResults([...tests, ...packages].sort((a, b) => Number(a.from_price) - Number(b.from_price)));
        })
        .catch(() => setError("Couldn't load results, try again."))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const toSelectionItem = (result) => {
    if (result.kind === "package") {
      return { name: result.name, test_count: result.test_count, is_package: 1, from_price: result.from_price, lab_name: result.lab_name };
    }
    return result.source === "unmapped"
      ? { test_name: result.name, name: result.name, test_count: result.test_count, is_package: 0, is_unmapped: true, from_price: result.from_price }
      : { test_master_id: result.id, name: result.name, test_count: result.test_count, is_package: 0, from_price: result.from_price };
  };

  const handleAdd = (result) => {
    const item = toSelectionItem(result);
    isTestSelected(item) ? removeTest(item) : addTest(item);
  };

  const handleChipClick = (chipLabel) => { setQuery(chipLabel); setOpen(true); };

  // Location button label.
  const locationLabel = location?.city
    ? location.city
    : "Add location";

  return (
    <>
      <div ref={containerRef} className="relative">
        <div className="flex gap-3">
          {/* Test search input */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#13B58E]" size={18} />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search tests, health packages, symptoms..."
              className="w-full h-12 pl-11 pr-4 rounded-full border border-gray-200 bg-white text-[14px] text-[#1A1A1A] placeholder:text-[#9AA6A4] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
            />
          </div>

          {/* Location button (replaces Search button) */}
          <button
            onClick={() => setShowLocationModal(true)}
            className="h-12 px-5 rounded-full bg-[#13B58E] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#0fa07d] transition shrink-0"
          >
            <MapPin size={15} />
            <span className="max-w-[120px] truncate">{locationLabel}</span>
          </button>
        </div>

        {/* Popular test chips */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-[12px] text-[#6B7A99]">Popular:</span>
          {POPULAR_CHIPS.map((chip) => (
            <button
              key={chip}
              onClick={() => handleChipClick(chip)}
              className="text-[12px] px-3 py-1.5 rounded-full border border-gray-200 text-[#3D4945] hover:border-[#13B58E] hover:text-[#13B58E] transition"
            >
              {chip}
            </button>
          ))}
        </div>

        {/* Search dropdown */}
        {open && query.trim() && (
          <div className="absolute left-0 right-0 mt-3 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-30">
            {loading && (
              <div className="flex items-center justify-center gap-2 px-4 py-5 text-[13px] text-[#6B7A99]">
                <Loader2 size={15} className="animate-spin" /> Searching...
              </div>
            )}
            {!loading && error && <p className="px-4 py-4 text-[13px] text-red-500">{error}</p>}
            {!loading && !error && results.length === 0 && (
              <p className="px-4 py-4 text-[13px] text-[#6B7A99]">No tests or packages found for "{query}".</p>
            )}
            {!loading && !error && results.length > 0 && (
              <div className="max-h-[360px] overflow-y-auto">
                {results.map((result) => {
                  const selected = isTestSelected(toSelectionItem(result));
                  return (
                    <div
                      key={`${result.kind}:${result.source ?? ""}:${result.id ?? result.name}`}
                      className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#EEF3F2] flex items-center justify-center text-[14px] shrink-0">
                        {result.kind === "package" ? "📦" : "🧪"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-[#191C1D] truncate">{result.name}</p>
                        <p className="text-[11px] text-[#6B7A99] truncate">
                          {result.kind === "package"
                            ? `${result.test_count} Tests Included${result.lab_name ? ` · ${result.lab_name}` : ""}`
                            : result.test_count
                            ? `${result.test_count} parameters`
                            : result.body_part || result.disease_name || ""}
                        </p>
                      </div>
                      <p className="text-[13px] font-bold text-[#191C1D] shrink-0">
                        From ₹{formatPrice(result.from_price)}
                      </p>
                      <button
                        onClick={() => handleAdd(result)}
                        className={`flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-full transition shrink-0 ${
                          selected
                            ? "bg-[#EEF3F2] text-[#13B58E]"
                            : "bg-[#13B58E] text-white hover:bg-[#0fa07d]"
                        }`}
                      >
                        {selected ? "Added" : <><Plus size={13} /> Add</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Location modal */}
      {showLocationModal && <LocationModal onClose={() => setShowLocationModal(false)} />}
    </>
  );
};

export default LabSearchBar;
